import { Typography } from "@mui/material";
import * as React from "react";

import { useNavigation } from "@/providers/navigation";
import { RobotoButton } from "@/shared/components";
import {
  IngestionStatus,
  MAX_FILE_SIZE_PREVIEW,
  FileRecord,
} from "@/shared/domain/files/FileRecord";
import {
  isImage,
  isText,
  isMarkdown,
  isVideo,
  isWebRenderable,
  canHaveTopics,
} from "@/shared/domain/files/fileTypes";
import {
  useFileSuspense,
  useSignedUrl,
} from "@/shared/domain/files/hooks/useFile";

import styles from "./FileContent.module.css";
import { IframeContent } from "./IframeContent";
import { ImageContent } from "./ImageContent";
import { TextContent } from "./TextContent";
import { VideoContent } from "./VideoContent";

interface FileContentProps {
  fileId: string;
}

export function FileContent({ fileId }: FileContentProps) {
  const { goto } = useNavigation();

  const { data: fileRecord } = useFileSuspense(fileId);

  const signedUrlQuery = useSignedUrl(fileRecord);

  const contentType = getContentType(fileRecord);

  switch (contentType) {
    case "image":
      return (
        <ImageContent fileRecord={fileRecord} signedUrl={signedUrlQuery.data} />
      );

    case "video":
      return (
        <VideoContent fileRecord={fileRecord} signedUrl={signedUrlQuery.data} />
      );

    case "text":
      return (
        <TextContent fileRecord={fileRecord} signedUrl={signedUrlQuery.data} />
      );

    case "text-too-large":
      return (
        <MsgContent>
          File size is over 50 MB, skipping preview <br />
          <span className={styles.secondaryText}>
            Open or Download the file using the buttons above
          </span>
        </MsgContent>
      );

    case "web":
      return <IframeContent signedUrl={signedUrlQuery.data} />;

    case "no-preview-ingested":
      return (
        <MsgContent>
          No preview available for this file type.
          <br />
          <span className={styles.secondaryText}>
            <RobotoButton
              eventName={"VisualizeFileClicked"}
              variant={"text"}
              color="primary"
              onClick={() => {
                goto.visualize({
                  dataSource: {
                    files: [fileRecord.file_id],
                  },
                });
              }}
              eventProperties={{
                fileId: fileRecord.file_id,
              }}
            >
              Open in Visualizer
            </RobotoButton>
          </span>
        </MsgContent>
      );

    case "not-ingested":
      return (
        <MsgContent>
          File not ingested yet
          <br />
          <span className={styles.secondaryText}>
            This file hasn&apos;t been ingested, so there aren&apos;t any topics
            available for visualization. Check if any ingestion actions are
            currently running on its dataset, or invoke one manually.
          </span>
        </MsgContent>
      );

    case "no-preview":
      return (
        <MsgContent>
          No preview available <br />
          <span className={styles.secondaryText}>
            Open or Download the file using the buttons above
          </span>
        </MsgContent>
      );
  }
}

type ContentType =
  | "image"
  | "video"
  | "text-too-large"
  | "text"
  | "web"
  | "no-preview-ingested"
  | "not-ingested"
  | "no-preview";

function getContentType(fileRecord: FileRecord): ContentType {
  if (isImage(fileRecord.relative_path)) {
    return "image";
  }
  if (isVideo(fileRecord.relative_path)) {
    return "video";
  }
  if (
    isText(fileRecord.relative_path) ||
    isMarkdown(fileRecord.relative_path)
  ) {
    return fileRecord.size > MAX_FILE_SIZE_PREVIEW ? "text-too-large" : "text";
  }
  if (isWebRenderable(fileRecord.relative_path)) {
    return "web";
  }
  if (fileRecord.ingestion_status !== IngestionStatus.NotIngested) {
    return "no-preview-ingested";
  }
  if (canHaveTopics(fileRecord.relative_path)) {
    return "not-ingested";
  }
  return "no-preview";
}

function MsgContent({ children }: React.PropsWithChildren) {
  return (
    <div className={styles.msgContentContainer}>
      <Typography className={styles.msgContentText}>{children}</Typography>
    </div>
  );
}
